/**
 * Lightweight client-side router for PitchPilot SPA.
 *
 * No library needed — just reads window.location.pathname and
 * provides a navigate() helper that pushes to history + triggers re-render.
 */

import { useSyncExternalStore } from 'react';

// ---------------------------------------------------------------------------
// Route types
// ---------------------------------------------------------------------------

export type RoutePage = 'workspace' | 'deck' | 'settings' | 'admin' | 'login';

export interface Route {
  page: RoutePage;
  params: Record<string, string>;
}

// ---------------------------------------------------------------------------
// Route matching
// ---------------------------------------------------------------------------

/** Parse the current pathname into a Route. */
function matchRoute(pathname: string): Route {
  // /deck/:deckId
  const deckMatch = pathname.match(/^\/deck\/([^/]+)\/?$/);
  if (deckMatch) {
    return { page: 'deck', params: { deckId: deckMatch[1] } };
  }

  // /w/:workspaceId
  const wsMatch = pathname.match(/^\/w\/([^/]+)\/?$/);
  if (wsMatch) {
    return { page: 'workspace', params: { workspaceId: wsMatch[1] } };
  }

  // /settings
  if (pathname === '/settings' || pathname === '/settings/') {
    return { page: 'settings', params: {} };
  }

  // /admin
  if (pathname === '/admin' || pathname === '/admin/') {
    return { page: 'admin', params: {} };
  }

  // /login
  if (pathname === '/login' || pathname === '/login/') {
    return { page: 'login', params: {} };
  }

  // / or anything else → workspace
  return { page: 'workspace', params: {} };
}

// ---------------------------------------------------------------------------
// External store for useSyncExternalStore
// ---------------------------------------------------------------------------

type Listener = () => void;
const listeners = new Set<Listener>();
let currentRoute: Route = matchRoute(window.location.pathname);

function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): Route {
  return currentRoute;
}

function notifyListeners(): void {
  currentRoute = matchRoute(window.location.pathname);
  for (const fn of listeners) fn();
}

// Listen for browser back/forward
window.addEventListener('popstate', notifyListeners);

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** Navigate to a new path. Pushes to browser history and triggers re-render. */
export function navigate(path: string, options?: { replace?: boolean }): void {
  if (path === window.location.pathname) return;
  if (options?.replace) {
    window.history.replaceState(null, '', path);
  } else {
    window.history.pushState(null, '', path);
  }
  notifyListeners();
}

/** React hook — returns the current Route. Re-renders on navigation. */
export function useRoute(): Route {
  return useSyncExternalStore(subscribe, getSnapshot);
}
